import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, X, Send, CheckCircle2, MessageCircle } from "lucide-react";
import { PageHero } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { IMAGES, COMPANY } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQ_TYPES = ["Industrial Pattern", "Wooden Pattern", "Metal Pattern", "Dies & Tooling", "CNC Machining", "Custom Engineering", "Manufacturing & Sourcing"];
const MATERIALS = ["Wood", "Metal", "MS", "SS", "Aluminium", "Not Sure"];

const Label = ({ children }) => (
  <label className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2 block">{children}</label>
);
const inputCls = "w-full bg-white border border-[#DCE1E8] text-[#0E1626] p-4 rounded-none focus:border-[#1D4ED8] focus:outline-none focus:ring-1 focus:ring-[#1D4ED8] transition-colors duration-200 placeholder:text-slate-400";

export default function Enquiry() {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", requirement_type: "", material: "", quantity: "", delivery_date: "", details: "" });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = (e) => {
    setFiles((prev) => [...prev, ...Array.from(e.target.files)]);
    e.target.value = "";
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const sendWhatsApp = () => {
    if (!form.name) {
      toast.error("Please enter your name first.");
      return;
    }
    const lines = [
      `*New Quote Request — ${COMPANY.name}*`,
      `Name: ${form.name}`,
      form.company && `Company: ${form.company}`,
      form.phone && `Phone: ${form.phone}`,
      form.email && `Email: ${form.email}`,
      form.requirement_type && `Requirement: ${form.requirement_type}`,
      form.material && `Material: ${form.material}`,
      form.quantity && `Quantity: ${form.quantity}`,
      form.delivery_date && `Delivery Date: ${form.delivery_date}`,
      form.details && `Details: ${form.details}`,
    ].filter(Boolean);
    if (files.length) lines.push(`(I will share ${files.length} drawing/file(s) here on WhatsApp)`);
    const msg = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${COMPANY.whatsapp}?text=${msg}`, "_blank");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/enquiries`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Enquiry submitted. Our team will get back to you shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div>
        <PageHero index="06 — Enquiry" title="Request A Quote" image={IMAGES.cncCloseup} />
        <div className="max-w-[900px] mx-auto px-5 md:px-10 py-32 text-center" data-testid="enquiry-success">
          <CheckCircle2 size={64} className="text-[#1D4ED8] mx-auto" />
          <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-8 text-[#0E1626]">Enquiry Received</h2>
          <p className="text-lg text-slate-600 font-light mt-6 max-w-xl mx-auto">Thank you for reaching out to JM Mevada Pattern Works. Our engineering team will review your requirement and respond with a quotation shortly.</p>
          <button onClick={() => { setDone(false); setForm({ name: "", company: "", phone: "", email: "", requirement_type: "", material: "", quantity: "", delivery_date: "", details: "" }); setFiles([]); }} className="btn-outline mt-10" data-testid="new-enquiry-button">Submit Another Enquiry</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHero index="06 — Enquiry" title={<>Request<br />A Quote.</>} subtitle="Share your drawings, samples and requirements. We'll respond with a detailed quotation." image={IMAGES.cncCloseup} />

      <section className="max-w-[1100px] mx-auto px-5 md:px-10 py-24 md:py-28">
        <Reveal>
          <form onSubmit={submit} className="space-y-8" data-testid="rfq-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div><Label>Name *</Label><input className={inputCls} value={form.name} onChange={set("name")} data-testid="rfq-name" placeholder="Your full name" /></div>
              <div><Label>Company Name</Label><input className={inputCls} value={form.company} onChange={set("company")} data-testid="rfq-company" placeholder="Company / Foundry" /></div>
              <div><Label>Phone Number *</Label><input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="rfq-phone" placeholder="+91 ..." /></div>
              <div><Label>Email *</Label><input type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="rfq-email" placeholder="you@company.com" /></div>

              <div>
                <Label>Requirement Type</Label>
                <Select value={form.requirement_type} onValueChange={(v) => setForm((f) => ({ ...f, requirement_type: v }))}>
                  <SelectTrigger className={inputCls + " h-auto flex justify-between"} data-testid="rfq-requirement-type"><SelectValue placeholder="Select requirement" /></SelectTrigger>
                  <SelectContent className="bg-white border-[#DCE1E8] text-[#0E1626] rounded-none">
                    {REQ_TYPES.map((r) => <SelectItem key={r} value={r} className="focus:bg-[#F4F6F9] rounded-none">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Material</Label>
                <Select value={form.material} onValueChange={(v) => setForm((f) => ({ ...f, material: v }))}>
                  <SelectTrigger className={inputCls + " h-auto flex justify-between"} data-testid="rfq-material"><SelectValue placeholder="Select material" /></SelectTrigger>
                  <SelectContent className="bg-white border-[#DCE1E8] text-[#0E1626] rounded-none">
                    {MATERIALS.map((m) => <SelectItem key={m} value={m} className="focus:bg-[#F4F6F9] rounded-none">{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div><Label>Quantity</Label><input className={inputCls} value={form.quantity} onChange={set("quantity")} data-testid="rfq-quantity" placeholder="e.g. 5 patterns" /></div>
              <div><Label>Delivery Date</Label><input type="date" className={inputCls} value={form.delivery_date} onChange={set("delivery_date")} data-testid="rfq-delivery-date" /></div>
            </div>

            <div><Label>Requirement Details</Label><textarea rows={5} className={inputCls} value={form.details} onChange={set("details")} data-testid="rfq-details" placeholder="Describe your pattern / machining requirement, dimensions, tolerances..." /></div>

            <div>
              <Label>Upload Drawings, 3D Models, Specs & Reference Files</Label>
              <label className="border border-dashed border-[#B8C0CB] bg-white p-10 flex flex-col items-center justify-center cursor-pointer hover:border-[#1D4ED8] transition-colors duration-300" data-testid="rfq-file-dropzone">
                <UploadCloud size={30} className="text-[#1D4ED8]" />
                <span className="mt-3 text-slate-500 font-light">Click to upload files (PDF, DWG, STEP, images...)</span>
                <input type="file" multiple className="hidden" onChange={onFiles} data-testid="rfq-file-input" />
              </label>
              {files.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="rfq-file-list">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between border border-[#DCE1E8] px-4 py-3 bg-white">
                      <span className="font-mono text-xs text-slate-700 truncate">{f.name} <span className="text-slate-400">· {(f.size / 1024).toFixed(0)} KB</span></span>
                      <button type="button" onClick={() => removeFile(i)} data-testid={`remove-file-${i}`} className="text-slate-400 hover:text-[#0E1626] transition-colors"><X size={16} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button type="submit" disabled={loading} className="btn-primary justify-center w-full sm:w-auto disabled:opacity-60" data-testid="rfq-submit">
                {loading ? "Submitting..." : <>Submit Enquiry <Send size={16} /></>}
              </button>
              <button type="button" onClick={sendWhatsApp} data-testid="rfq-whatsapp" className="justify-center w-full sm:w-auto inline-flex items-center gap-2 bg-[#25D366] text-white px-8 py-4 font-semibold text-[0.8rem] tracking-[0.14em] uppercase hover:bg-[#20bd5a] transition-colors duration-300">
                <MessageCircle size={16} /> Send via WhatsApp
              </button>
            </div>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
